import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Upload } from "lucide-react";
import { api, imageUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
  name: "", category: "unisex", description: "", short_description: "", tagline: "", occasion: "",
  notes: "", image_url: "", rollon_image_url: "",
  featured: false, available: true, is_bestseller: false,
  sizes: [{ label: "30 mL Bottle", price: 40, stock: 50 }, { label: "Roll-On", price: 10, stock: 100 }],
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing(JSON.parse(JSON.stringify(EMPTY)));
  const openEdit = (p) => setEditing({
    ...EMPTY, ...p,
    short_description: p.short_description || "", tagline: p.tagline || "", occasion: p.occasion || "",
    notes: p.notes || "", rollon_image_url: p.rollon_image_url || "",
    sizes: p.sizes.map((s) => ({ ...s })),
  });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: editing.name, category: editing.category, description: editing.description,
      short_description: editing.short_description, tagline: editing.tagline, occasion: editing.occasion,
      notes: editing.notes, image_url: editing.image_url, rollon_image_url: editing.rollon_image_url || "",
      featured: editing.featured, available: editing.available, is_bestseller: editing.is_bestseller,
      sizes: editing.sizes.map((s) => ({ label: s.label, price: Number(s.price), stock: Number(s.stock) })),
    };
    try {
      if (editing.id) {
        await api.put(`/admin/products/${editing.id}`, payload);
      } else {
        await api.post("/admin/products", payload);
      }
      toast.success("Product saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSaving(false);
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}" permanently?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadImage = (field) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((prev) => ({ ...prev, [field]: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const imageField = (field, label, testid) => (
    <div key={field}>
      <label className="font-sans-body text-[10px] uppercase tracking-[0.25em] text-[#C6A15B] block mb-2">{label}</label>
      <div className="flex items-center gap-4">
        {editing[field] && <img src={imageUrl(editing[field])} alt="" className="w-16 h-20 object-cover gold-hairline" />}
        <label className="btn-outline-gold cursor-pointer" data-testid={`${testid}-upload-label`}>
          <Upload size={14} /> Upload Image
          <input type="file" accept="image/*" className="hidden" onChange={uploadImage(field)} data-testid={`${testid}-upload-input`} />
        </label>
        {editing[field] && (
          <button type="button" onClick={() => setEditing({ ...editing, [field]: "" })} className="text-xs text-red-400">Remove</button>
        )}
      </div>
    </div>
  );

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif-display text-3xl text-white font-light">Products</h1>
        <button className="btn-gold" onClick={openNew} data-testid="admin-add-product-button">
          <Plus size={14} /> Add Product
        </button>
      </div>

      <div className="gold-hairline bg-[#0D0D0D] overflow-x-auto">
        <table className="w-full text-left font-sans-body text-sm" data-testid="admin-products-table">
          <thead>
            <tr className="border-b border-[#D4AF37]/15 text-[10px] uppercase tracking-[0.2em] text-[#C6A15B]">
              <th className="px-5 py-4">Product</th>
              <th className="px-5 py-4">Category</th>
              <th className="px-5 py-4">Sizes / Stock</th>
              <th className="px-5 py-4">Sold</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4"></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b border-[#D4AF37]/8 hover:bg-[#121212]" data-testid={`admin-product-row-${p.id}`}>
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-12 shrink-0 overflow-hidden gold-hairline bg-[#080808]">
                      {p.image_url && <img src={imageUrl(p.image_url)} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div>
                      <p className="text-white">{p.name}</p>
                      <p className="text-[10px] text-[#666666]">{p.featured ? "Featured" : ""}{p.is_bestseller ? " · Bestseller" : ""}</p>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-4 text-[#B8B8B8] capitalize">{p.category}</td>
                <td className="px-5 py-4 text-[#B8B8B8]">
                  {p.sizes.map((s) => <p key={s.label} className="text-xs">{s.label} — ${s.price} ({s.stock})</p>)}
                </td>
                <td className="px-5 py-4 font-mono-price text-white">{p.sales_count || 0}</td>
                <td className="px-5 py-4">
                  <span className={`text-[10px] uppercase tracking-[0.15em] ${p.available ? "text-emerald-400" : "text-red-400"}`}>
                    {p.available ? "Live" : "Hidden"}
                  </span>
                </td>
                <td className="px-5 py-4">
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => openEdit(p)} className="p-2 text-[#B8B8B8] hover:text-[#D4AF37]" data-testid={`admin-edit-product-${p.id}`} aria-label="Edit">
                      <Pencil size={15} />
                    </button>
                    <button onClick={() => remove(p)} className="p-2 text-[#B8B8B8] hover:text-red-400" data-testid={`admin-delete-product-${p.id}`} aria-label="Delete">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="product-edit-modal">
          <form onSubmit={save} className="gold-hairline bg-[#0D0D0D] p-8 w-full max-w-2xl space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="font-serif-display text-2xl text-white">{editing.id ? "Edit Product" : "New Product"}</h2>
              <button type="button" onClick={() => setEditing(null)} aria-label="Close" className="text-[#B8B8B8] hover:text-white"><X size={20} /></button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="font-sans-body text-[10px] uppercase tracking-[0.25em] text-[#C6A15B] block mb-2">Name</label>
                <input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3 font-sans-body text-sm text-white focus:outline-none focus:border-[#D4AF37]"
                  data-testid="product-name-input" />
              </div>
              <div>
                <label className="font-sans-body text-[10px] uppercase tracking-[0.25em] text-[#C6A15B] block mb-2">Category</label>
                <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  className="w-full bg-[#080808] border border-[#D4AF37]/25 px-4 py-3 font-sans-body text-sm text-white focus:outline-none focus:border-[#D4AF37]"
                  data-testid="product-category-select">
                  <option value="mens">Men's</option>
                  <option value="womens">Women's</option>
                  <option value="unisex">Unisex</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="font-sans-body text-[10px] uppercase tracking-[0.25em] text-[#C6A15B] block mb-2">Tagline</label>
                <input value={editing.tagline} onChange={(e) => setEditing({ ...editing, tagline: e.target.value })}
                  placeholder="e.g. The entrance everyone notices."
                  className="w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3 font-sans-body text-sm text-white placeholder:text-[#666666] focus:outline-none focus:border-[#D4AF37]"
                  data-testid="product-tagline-input" />
              </div>
              <div>
                <label className="font-sans-body text-[10px] uppercase tracking-[0.25em] text-[#C6A15B] block mb-2">Suggested Occasion</label>
                <input value={editing.occasion} onChange={(e) => setEditing({ ...editing, occasion: e.target.value })}
                  placeholder="e.g. Evenings out, first impressions"
                  className="w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3 font-sans-body text-sm text-white placeholder:text-[#666666] focus:outline-none focus:border-[#D4AF37]"
                  data-testid="product-occasion-input" />
              </div>
            </div>

            <div>
              <label className="font-sans-body text-[10px] uppercase tracking-[0.25em] text-[#C6A15B] block mb-2">Short Description (shown on product cards)</label>
              <input value={editing.short_description} onChange={(e) => setEditing({ ...editing, short_description: e.target.value })}
                placeholder="One elegant sentence..."
                className="w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3 font-sans-body text-sm text-white placeholder:text-[#666666] focus:outline-none focus:border-[#D4AF37]"
                data-testid="product-short-description-input" />
            </div>

            <div>
              <label className="font-sans-body text-[10px] uppercase tracking-[0.25em] text-[#C6A15B] block mb-2">Full Description (product page)</label>
              <textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                className="w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3 font-sans-body text-sm text-white focus:outline-none focus:border-[#D4AF37] resize-none"
                data-testid="product-description-input" />
            </div>
            <div>
              <label className="font-sans-body text-[10px] uppercase tracking-[0.25em] text-[#C6A15B] block mb-2">Fragrance Notes</label>
              <textarea rows={2} value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                className="w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3 font-sans-body text-sm text-white focus:outline-none focus:border-[#D4AF37] resize-none"
                data-testid="product-notes-input" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {imageField("image_url", "30 mL Bottle Image", "product-spray-image")}
              {imageField("rollon_image_url", "Roll-On Image", "product-rollon-image")}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="font-sans-body text-[10px] uppercase tracking-[0.25em] text-[#C6A15B]">Sizes, Prices &amp; Stock</label>
                <button type="button" onClick={() => setEditing({ ...editing, sizes: [...editing.sizes, { label: "", price: 0, stock: 0 }] })}
                  className="font-sans-body text-[10px] uppercase tracking-[0.15em] text-[#D4AF37]" data-testid="add-size-button">
                  + Add Size
                </button>
              </div>
              <div className="space-y-2">
                {editing.sizes.map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <input placeholder="Size (e.g. 30 mL Bottle)" value={s.label}
                      onChange={(e) => { const sizes = [...editing.sizes]; sizes[i] = { ...s, label: e.target.value }; setEditing({ ...editing, sizes }); }}
                      className="col-span-5 bg-transparent border border-[#D4AF37]/25 px-3 py-2.5 font-sans-body text-xs text-white focus:outline-none focus:border-[#D4AF37]"
                      data-testid={`size-label-${i}`} />
                    <input type="number" min="0" step="0.01" placeholder="Price" value={s.price}
                      onChange={(e) => { const sizes = [...editing.sizes]; sizes[i] = { ...s, price: e.target.value }; setEditing({ ...editing, sizes }); }}
                      className="col-span-3 bg-transparent border border-[#D4AF37]/25 px-3 py-2.5 font-sans-body text-xs text-white focus:outline-none focus:border-[#D4AF37]"
                      data-testid={`size-price-${i}`} />
                    <input type="number" min="0" placeholder="Stock" value={s.stock}
                      onChange={(e) => { const sizes = [...editing.sizes]; sizes[i] = { ...s, stock: e.target.value }; setEditing({ ...editing, sizes }); }}
                      className="col-span-3 bg-transparent border border-[#D4AF37]/25 px-3 py-2.5 font-sans-body text-xs text-white focus:outline-none focus:border-[#D4AF37]"
                      data-testid={`size-stock-${i}`} />
                    <button type="button" onClick={() => setEditing({ ...editing, sizes: editing.sizes.filter((_, j) => j !== i) })}
                      className="col-span-1 text-[#B8B8B8] hover:text-red-400" aria-label="Remove size">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-6 pt-2">
              {[["featured", "Feature on Homepage"], ["is_bestseller", "Mark as Bestseller"], ["available", "Available in Store"]].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 font-sans-body text-xs text-[#B8B8B8] cursor-pointer">
                  <input type="checkbox" checked={editing[k]} onChange={(e) => setEditing({ ...editing, [k]: e.target.checked })}
                    className="accent-[#D4AF37]" data-testid={`product-${k}-checkbox`} />
                  {label}
                </label>
              ))}
            </div>

            <button type="submit" className="btn-gold w-full" disabled={saving} data-testid="product-save-button">
              {saving ? "Saving..." : "Save Product"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
